import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { SDGS } from "@/data/site";

const COMMITMENTS = [
  { title: "Clean Energy", text: "Developing carbon-free generation technology as a direct alternative to fossil-fuel generators." },
  { title: "Climate Action", text: "Every product decision is weighed against its emissions and environmental footprint." },
  { title: "Energy Access", text: "Designing for the hundreds of millions who lack reliable, affordable power." },
  { title: "Sustainable Development", text: "Technology that supports education, health, agriculture and enterprise." },
  { title: "Environmental Responsibility", text: "Responsible materials, lifecycle thinking and honest environmental claims." },
  { title: "Innovation for Communities", text: "Engineering that starts from community realities, not laboratory assumptions." },
  { title: "Africa's Energy Future", text: "Building African clean-tech capability that competes on the world stage." },
];

export default function Sustainability() {
  return (
    <>
      <Seo
        title="Sustainability — BNM Sustainable Energy Limited"
        description="BNM's sustainability commitments: clean energy, climate action, energy access and responsible innovation — aligned with UN Sustainable Development Goals 7, 9, 11 and 13."
      />
      <PageHero
        testid="sustainability-hero"
        dark={false}
        eyebrow="Sustainability"
        lines={["Power that respects", "the planet it", "serves."]}
        lead="Sustainability is not a programme at BNM — it is the product. Our technology exists to replace carbon-intensive power with clean alternatives."
      />

      <section data-testid="sustainability-commitments" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Our Commitments"
            title="Seven principles of responsible energy development."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05} className={i === COMMITMENTS.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sustainability-sdg" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Global Alignment"
            title="Aligned with the UN Sustainable Development Goals."
            lead="BNM's mission contributes to four SDGs. This reflects alignment of purpose — BNM does not claim official UN partnership or endorsement."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {SDGS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <article className="h-full rounded-lg border border-white/10 bg-pine/60 p-7">
                  <p className="font-display text-4xl font-extrabold text-volt">SDG {s.n}</p>
                  <h3 className="mt-3 font-display text-base font-bold tracking-tight text-white">{s.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.note}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Partner on sustainable energy access."
        text="Development organisations, NGOs and institutions working on energy access and climate action — we would like to work with you."
        primary={{ label: "Explore Partnership", to: "/partners", testid: "sustainability-partner-cta" }}
        secondary={{ label: "Contact BNM", to: "/contact", testid: "sustainability-contact-cta" }}
        testid="sustainability-cta"
      />
    </>
  );
}
